import { Component, inject, PLATFORM_ID, Input, Output, EventEmitter, ElementRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { ToastService } from '../../../../layout/toast/toast.service';
import { LanguageService } from '../../../../core/services/language/language.service';
import { UserRatingService } from '../../../../core/services/users/userRating.service';
import { getStoredAuthToken } from '../../../../core/auth/auth-token';

@Component({
  selector: 'app-kebab-rating-component',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './kebab-rating-component.html',
  styleUrl: './kebab-rating-component.scss'
})
export class KebabRatingComponent {
  @Input() data: any;
  @Output() toggleChange = new EventEmitter<void>();

  private toast = inject(ToastService);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private userRatingService = inject(UserRatingService);
  private platformId = inject<Object>(PLATFORM_ID);
  private el = inject(ElementRef);

  hoveredStar = 0;
  selectedStar = 0;
  showStars = false;
  isSubmitting = false;
  openSide: 'left' | 'right' = 'left';

  readonly stars = [1, 2, 3, 4, 5];

  toggleStars(event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (isPlatformBrowser(this.platformId)) {
      const token = getStoredAuthToken();
      if (!token) {
        this.toast.warning(
          this.languageService.t('loginRequiredTitle') || 'Login Required',
          this.languageService.t('loginRequiredMessage') || 'Please log in to perform this activity.'
        );
        this.toggleChange.emit();
        this.router.navigate(['/auth'], { queryParams: { redirect: this.router.url || '/' } });
        return;
      }
    }

    if (!this.showStars && isPlatformBrowser(this.platformId)) {
      const host = this.el.nativeElement as HTMLElement;
      const rect = host.getBoundingClientRect();
      if (rect.left < 235) {
        this.openSide = 'right';
      } else {
        this.openSide = 'left';
      }
    }
    this.showStars = !this.showStars;
  }

  rate(star: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (isPlatformBrowser(this.platformId)) {
      const token = getStoredAuthToken();
      if (!token) {
        this.toast.warning(
          this.languageService.t('loginRequiredTitle') || 'Login Required',
          this.languageService.t('loginRequiredMessage') || 'Please log in to perform this activity.'
        );
        this.toggleChange.emit();
        this.router.navigate(['/auth'], { queryParams: { redirect: this.router.url || '/' } });
        return;
      }
    }

    if (!this.data || this.isSubmitting) return;

    this.selectedStar = star;
    this.isSubmitting = true;

    const title = this.data?.title || this.data?.original_title ||
                 this.data?.original_name || this.data?.name || 'Item';
    const mediaType = this.data?.media_type || (this.data?.title ? 'movie' : 'tv');

    // Close menu immediately
    this.toggleChange.emit();

    // Backend expects rating 1-10 (each star = 2 points)
    const backendRating = star * 2;

    this.userRatingService.addRating(this.data, backendRating, mediaType).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toast.success('Rated!', `You gave "${title}" ${star}/5 stars.`);
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('Rating error:', err);
        const msg = err?.status === 401 || err?.status === 404
          ? (this.languageService.t('loginRequiredMessage') || 'Please sign in to rate titles.')
          : 'Could not save rating. Please try again.';
        this.toast.error('Rating Failed', msg);
      }
    });
  }

  onHoverStar(star: number) {
    this.hoveredStar = star;
  }

  onLeave() {
    this.hoveredStar = 0;
  }

  getStarLabel(star: number): string {
    switch (star) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Masterpiece!';
      default: return '';
    }
  }
}
